import { NavLink } from 'react-router-dom'
import { FilterMatchMode } from 'primereact/api'
import { ButtonGroup } from 'react-bootstrap'
import { useState } from 'react'
import DeleteDialog from '../../../../common/dialogs/DeleteDialog'
import PageHeader from '../../../../layout/page/PageHeader'
import DataTable from '../../../../common/data-table/DataTable'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit } from '@fortawesome/free-solid-svg-icons'
import { useAppSelector } from '../../../../../store/hooks'
import TrueFalseCell from '../../../../common/data-table/TrueFalseCell'

type TableRow = Record<string, unknown> & { id?: number; name?: string }
type StatusLabels = { trueLabel: string; falseLabel: string }

const ClientThemes = () => {
    const theme = useAppSelector((state) => state.theme.theme)
    const [refreshTable, setRefreshTable] = useState(false)

    const actionsBodyTemplate = (rowData: TableRow) => {
        return (
            <ButtonGroup size="sm">
                <DeleteDialog
                    url={'/administration/client/theme/delete/' + rowData.id}
                    record={rowData}
                    deletedCallback={() => setRefreshTable(true)}
                    message={
                        <p className="mb-3 pb-3 border-bottom">
                            Are you sure you want to delete theme{' '}
                            <strong> {rowData.name}</strong>?
                        </p>
                    }
                />
                <NavLink
                    to={'/administration/client/theme/view/' + rowData.id}
                    className="btn btn-outline-primary ms-1"
                >
                    <FontAwesomeIcon icon={faEdit} />
                </NavLink>
            </ButtonGroup>
        )
    }

    const statusTemplate = (rowData: TableRow, field: string, labels: StatusLabels) => {
        return (
            <TrueFalseCell
                value={rowData[field] === 1}
                trueLabel={labels.trueLabel}
                falseLabel={labels.falseLabel}
            />
        )
    }

    return (
        <>
            <PageHeader
                icon="paint-brush"
                subtitle="Customise the visual theme and appearance"
                gradient="linear-gradient(135deg, #a855f7, #9333ea)"
                header="Themes"
                buttons={[
                    {
                        variant: theme === 'dark' ? 'dark' : 'light',
                        label: 'Back to Administration',
                        icon: 'fa-solid fa-arrow-left',
                        url: '/administration',
                    },
                    {
                        variant: 'success',
                        label: 'Create New',
                        icon: 'fa-solid fa-plus',
                        url: '/administration/client/theme/new',
                    },
                ]}
            />
            <DataTable
                refreshTable={refreshTable}
                onRefreshed={() => setRefreshTable(false)}
                config={{
                    dataRoute: '/administration/client/theme',
                    first: 0,
                    rows: 10,
                    page: 0,
                    sortField: 'name',
                    sortOrder: 1,
                    filters: {
                        global: {
                            value: null,
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        name: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        code: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                    },
                    filterDisplay: 'menu',
                }}
                columns={[
                    {
                        field: 'code',
                        header: 'Theme Code',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        style: {
                            maxWidth: '8rem',
                        },
                    },
                    {
                        field: 'name',
                        header: 'Theme Name',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        style: {
                            maxWidth: '8rem',
                        },
                    },
                    {
                        field: 'active',
                        header: 'Active',
                        sortable: true,
                        filter: false,
                        body: (rowData: TableRow) =>
                            statusTemplate(rowData, 'active', {
                                trueLabel: 'Active',
                                falseLabel: 'Inactive',
                            }),
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                        style: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                    },
                    {
                        field: 'default',
                        header: 'Default',
                        sortable: true,
                        filter: false,
                        body: (rowData: TableRow) =>
                            statusTemplate(rowData, 'default', {
                                trueLabel: 'Default',
                                falseLabel: 'Not default',
                            }),
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                        style: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                    },
                    {
                        field: 'id',
                        header: 'Actions',
                        sortable: false,
                        body: actionsBodyTemplate,
                        alignHeader: 'center',
                        headerStyle: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        style: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        toggleVisibility: false,
                    },
                ]}
                globalFilterFields={['name', 'code']}
            />
        </>
    )
}

export default ClientThemes
